import styled from 'styled-components';

/** What sits between messages: day dividers, the unread marker, system notes. */

/** Full-width rounded pill: day dividers and the unread marker. */
export const ChatDivider = styled.div<{ $primary?: boolean; $first?: boolean }>`
  position: relative;
  align-self: center;
  /* The top margin separates the pill from the messages above it. The very
     first one has none above, only the floating header, and that gap is
     already the scroll area's padding. */
  margin: ${({ $first }) => ($first ? '0 0 8px' : '20px 0 8px')};
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 12px;
  line-height: 18px;
  background: ${({ theme, $primary }) =>
    $primary ? theme.primary : theme.mediumGray};
  color: ${({ theme, $primary }) => ($primary ? theme.white : theme.darkGray)};
`;

/**
 * "Loading older history" spinner, parked beside the topmost day pill.
 *
 * Absolutely positioned off the pill's right edge so it takes no space in the
 * flow: appearing must not shift the pill off centre or nudge the thread. The
 * previous version sat in the flow above the list with a negative margin, which
 * put the spinner straight on top of the pill.
 */
export const DividerSpinner = styled.div`
  position: absolute;
  left: 100%;
  top: 50%;
  /*
   * 100px because that is the box AdminSpinner draws: it scales only the ring
   * inside it, so a smaller wrapper makes the box flex-shrink and its own
   * overflow clip then eats most of the ring - which is why this showed as two
   * red slivers. The ring sits in the middle of that box; the offset brings it
   * back beside the pill.
   */
  width: 100px;
  height: 100px;
  transform: translate(-30px, -50%);
  pointer-events: none;
`;

/**
 * `side: 'system'` messages - "Ana added Ivan", "You renamed the group".
 *
 * Deliberately not a tinted success/error banner: these are bookkeeping, not an
 * outcome, and a green banner every time somebody joins a group reads as a
 * series of congratulations. Styled as a quiet centred line, the same weight as
 * a day divider's label without the pill around it - a group's history can carry
 * a lot of these.
 */
export const SystemNote = styled.div`
  width: 100%;
  margin: 12px 0;
  padding: 0 16px;
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};
`;

/**
 * Viktor could not answer - "Viktor is busy right now. Please try again in a
 * minute." The same quiet centred line as a system note, because it is one:
 * a fact about the conversation rather than a turn in it, which is why it is
 * not a bubble. Red because unlike a membership note it is an outcome, and
 * the one the reader has to act on.
 */
export const ErrorNote = styled(SystemNote)`
  color: ${({ theme }) => theme.primary};
`;
